import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowDown } from "lucide-react";
import heroBg from "@/assets/hero-bg.mp4";
import heroPoster from "@/assets/hero-poster.jpg";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import { getHomepageT } from "@/i18n/homepage";

const tickerValues = [
  { value: 700, suffix: "+" },
  { value: 1200, suffix: "+" },
  { value: 4500, suffix: "+" },
  { value: 200, suffix: "+" },
];

const CountUp = ({ target, suffix, duration = 1.5 }: { target: number; suffix: string; duration?: number }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let startTime: number | null = null;
    let rafId: number;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) {
        rafId = requestAnimationFrame(step);
      } else {
        setCount(target);
      }
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [inView, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const Hero = () => {
  const [videoReady, setVideoReady] = useState(false);
  const [videoSrc, setVideoSrc] = useState<string | undefined>(undefined);
  const handleCanPlay = useCallback(() => setVideoReady(true), []);
  const lang = useLanguage();
  const t = getHomepageT("hero", lang);
  

  // Skip animations on first paint to eliminate LCP render delay
  const isFirstMount = useRef(true);
  useEffect(() => { isFirstMount.current = false; }, []);
  const skip = isFirstMount.current;

  // Defer video loading until after initial render + idle time.
  // Skipped entirely on small screens, data-saver connections and when the
  // visitor prefers reduced motion — the poster image carries the design there.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const nav = navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } };
    const slow = nav.connection?.saveData === true || /2g/.test(nav.connection?.effectiveType ?? "");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const smallScreen = window.matchMedia("(max-width: 767px)").matches;
    if (slow || reduced || smallScreen) return;

    if ("requestIdleCallback" in window) {
      const id = (window as any).requestIdleCallback(() => setVideoSrc(heroBg), { timeout: 3000 });
      return () => (window as any).cancelIdleCallback(id);
    }
    const timer = setTimeout(() => setVideoSrc(heroBg), 2000);
    return () => clearTimeout(timer);
  }, []);


  return (
    <section className="relative min-h-screen flex flex-col justify-between bg-primary overflow-hidden">
      <div className="absolute inset-0">
        {/* High-priority poster image for fast LCP – visible until video loads */}
        <img
          src={heroPoster}
          alt=""
          role="presentation"
          fetchPriority="high"
          decoding="async"
          className={`absolute inset-0 w-full h-full object-cover opacity-75 transition-opacity duration-700 ${videoReady ? "opacity-0" : "opacity-75"}`}
        />
        {videoSrc && (
          <video
            src={videoSrc}
            autoPlay
            loop
            muted
            playsInline
            preload="none"
            onCanPlay={handleCanPlay}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${videoReady ? "opacity-75" : "opacity-0"}`}
          >
            <track kind="descriptions" label="Visual description" srcLang="en" />
          </video>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-qbd-oxford via-qbd-oxford/40 to-transparent" />
      </div>

      <div className="container mx-auto px-6 lg:px-10 relative z-10 flex-1 flex items-center pt-32 sm:pt-36 pb-16 sm:pb-20">
        <div className="max-w-4xl">
          <motion.div
            initial={skip ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mb-6 sm:mb-8 inline-flex items-center gap-4"
          >
            <span className="w-10 h-px bg-gradient-to-r from-qbd-blue to-transparent" />
            <span className="text-sm font-medium tracking-wide text-white/80">
              {t.tagline}
            </span>
          </motion.div>

          {skip ? (
            <h1 className="text-3xl sm:text-5xl lg:text-[50px] xl:text-[50px] font-bold leading-[1.1] tracking-tight mb-6 sm:mb-8">
              <span className="text-white">{t.heading}</span>
              <br />
              <span className="font-display font-normal text-qbd-blue inline-flex flex-wrap gap-x-[0.3em]">
                {t.words.map((word, i) => (
                  <span key={word} className="inline-block">{word}</span>
                ))}
              </span>
            </h1>
          ) : (
            <motion.h1
              className="text-3xl sm:text-5xl lg:text-[50px] xl:text-[50px] font-bold leading-[1.1] tracking-tight mb-6 sm:mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <span className="text-white">{t.heading}</span>
              <br />
              <span className="font-display font-normal text-qbd-blue inline-flex flex-wrap gap-x-[0.3em]">
                {t.words.map((word, i) => (
                  <motion.span
                    key={word}
                    initial={{ opacity: 0, y: 15, filter: "blur(8px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{ duration: 0.5, delay: 0.4 + i * 0.12, ease: "easeOut" }}
                    className="inline-block"
                  >
                    {word}
                  </motion.span>
                ))}
              </span>
            </motion.h1>
          )}

          {skip ? (
            <p className="text-base sm:text-xl text-white/70 max-w-xl mb-8 sm:mb-10 leading-relaxed">
              {t.body}
            </p>
          ) : (
            <motion.p
              className="text-base sm:text-xl text-white/70 max-w-xl mb-8 sm:mb-10 leading-relaxed"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
            >
              {t.body}
            </motion.p>
          )}

          <motion.div
            className="flex flex-wrap items-center gap-4"
            initial={skip ? false : { opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
          >
            <a
              href="#solutions"
              className="group inline-flex items-center gap-2 bg-qbd-blue text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-qbd-blue/90 hover:shadow-xl hover:shadow-qbd-blue/25 hover:scale-[1.03] transition-all duration-300 shadow-lg shadow-qbd-blue/20"
            >
              {t.cta}
              <ArrowDown size={15} className="group-hover:translate-y-0.5 transition-transform" />
            </a>
            <a
              href={localizeHref("/en/contact", lang)}
              className="inline-flex items-center gap-2 border border-white/30 text-white/90 px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-white/10 hover:border-white/50 transition-all duration-300 backdrop-blur-sm"
            >
              {t.contact}
            </a>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="relative z-10 border-t border-white/10 bg-qbd-oxford/60 backdrop-blur-md"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.8 }}
      >
        <div className="container mx-auto px-6 lg:px-10 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8">
            {tickerValues.map((item, i) => (
              <motion.div
                key={i}
                className="text-center sm:text-left"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 1 + i * 0.1 }}
              >
                <p className="text-2xl sm:text-3xl font-bold text-white">
                  <CountUp target={item.value} suffix={item.suffix} />
                </p>
                <p className="text-xs sm:text-sm text-white/40 mt-1">{t.ticker[i].label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
